import DialogCloseButton from '@/components/dialog/DialogCloseButton'
import { useNotification, useTranslator } from '@/hooks'
import { Dialog, DialogActions, DialogContent, Divider, TextField, Typography, Box } from '@mui/material'

import { Button } from '@/@core/components/mui/Button'

import { useForm, FormProvider, Controller } from 'react-hook-form'
import { CompanyStatusEnum, IBalanceTransactionRecord, ICompany, useBalanceTransactionRejectMutation } from '@/@data'
import { EmployerStatusDropDown } from '@/components/employer'
import CustomAvatar from '@/@core/components/mui/Avatar'
import { getInitials } from '@/utils/getInitials'

interface IRejectNoteBalanceDetail {
  open: boolean
  setOpen: (open: boolean) => void
  dataBalanceTransactionDetail: IBalanceTransactionRecord
  month?: string
  id?: string
}

export function RejectNoteBalanceDetail({
  open,
  setOpen,
  dataBalanceTransactionDetail,
  month,
  id
}: IRejectNoteBalanceDetail) {
  const { __ } = useTranslator()
  const n = useNotification()
  const { mutate: rejectBalanceTransaction } = useBalanceTransactionRejectMutation()
  const { transactionId } = useParams()
  const navigate = useNavigate()

  const handleClose = () => {
    setOpen(false)
  }

  const methods = useForm({
    defaultValues: {
      note: ''
    }
  })

  const onSubmit = (data: any) => {
    rejectBalanceTransaction(
      { id: Number(transactionId), note: data.note },
      {
        onSuccess: () => {
          n.success(__('Reject balance transaction successfully'))
          handleClose()
          navigate(`/cms/finance/balance/${id}?month=${month}`)
        },
        onError: error => {
          n.error(error.message)
        }
      }
    )
  }

  const getAvatar = (params: Pick<ICompany, 'logo_file' | 'name'>) => {
    const { logo_file, name } = params

    if (logo_file) {
      return <CustomAvatar src={logo_file.tiny_url} skin='light' size={80} />
    } else {
      return (
        <CustomAvatar skin='light' size={80}>
          {getInitials(name as string)}
        </CustomAvatar>
      )
    }
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='sm'
      scroll='body'
      fullWidth
      closeAfterTransition={false}
      sx={{
        '& .MuiDialog-paper': { overflow: 'visible', maxWidth: '1000px', borderRadius: '24px' }
      }}
    >
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <DialogCloseButton onClick={handleClose} sx={{ position: 'absolute', right: 0, top: 0, zIndex: 1302 }}>
            <i className='tabler-x' />
          </DialogCloseButton>

          <DialogContent>
            <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
              {getAvatar({
                logo_file: dataBalanceTransactionDetail?.company?.logo_file,
                name: dataBalanceTransactionDetail?.company?.name || '-'
              })}
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                  <Typography variant='h5' gutterBottom sx={{ fontWeight: 'medium', fontSize: '28px', mb: 0 }}>
                    {dataBalanceTransactionDetail?.company.name}
                  </Typography>
                  <EmployerStatusDropDown
                    status={dataBalanceTransactionDetail?.company?.status as CompanyStatusEnum}
                    isDetail={true}
                    sx={{
                      height: '30px',
                      fontSize: '13px'
                    }}
                  />
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <i className='tabler-id' />
                  <span className='text-[15px] leading-[22px]'>
                    {dataBalanceTransactionDetail?.company.system_code}
                  </span>

                  <i
                    className='tabler-copy'
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                      navigator.clipboard.writeText(dataBalanceTransactionDetail?.company.system_code || '')
                      n.success(__('Copied to clipboard!'))
                    }}
                  />
                </Box>
              </Box>
            </Box>
            <Box className='mb-4 border border-[#E0E0E0] rounded-[16px] p-6'>
              <div>
                <Typography variant='h6' component='span'>
                  {__('Note')}
                </Typography>
                <Divider className='my-4' />
              </div>
              <div className='flex flex-col gap-1'>
                <Typography className='text-[13px] leading-[15px] font-normal'>{__('Your Note')}</Typography>
                <Controller
                  name='note'
                  render={({ field }) => (
                    <TextField
                      {...field}
                      placeholder={__('Note')}
                      multiline
                      minRows={7}
                      fullWidth
                      sx={{
                        background: '#fff',
                        borderRadius: '10px',
                        '& textarea': {
                          fontSize: '17px',
                          color: '#A8AAAE'
                        }
                      }}
                    />
                  )}
                />
              </div>
            </Box>
          </DialogContent>

          <DialogActions className='px-[20px] sm:px-[24px]'>
            <Button
              variant='contained'
              className='text-[#6F83F6] bg-[#6F83F629] rounded-full h-[38px] shadow-none'
              onClick={handleClose}
            >
              {__('Cancel')}
            </Button>
            <Button
              type='submit'
              variant='contained'
              className='text-white bg-[#FF9900] rounded-full h-[38px] shadow-none'
              endIcon={<i className='tabler-check' />}
            >
              {__('Submit Reject')}
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  )
}
